import { renderWeatherIcon } from '@/Components/Common/deviceUtils';
import { AccuweatherIcon } from '@/Components/Environmental/AccuweatherIcon';
import { useWeatherData } from '@/Components/Environmental/EnvironmentalHooks';
import useUserInfo from '@/Components/Hooks/useUserInfo';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import ScheduleCard from './ScheduleCard';
import './WeatherCard.less';
import { useRegisterReportSection } from '../../reportUtils';

const WeatherCard = ({
  timezone,
  useruuid,
  hubRecord,
  measurementSystem,
  locale,
  is24HourFormat,
}) => {
  const tempUnit = useMemo(() => (measurementSystem === 'metric' ? 'C' : 'F'), [measurementSystem]);
  const [now, setNow] = useState(() => new Date());

  const { data: userInfo = {} } = useUserInfo({
    useruuid: useruuid,
  });
  const { outdoorWeather, isSuccess, isError } = useWeatherData(userInfo, hubRecord, tempUnit);

  const [showSkeleton, setShowSkeleton] = useState(true);

  useEffect(() => {
    if (isSuccess || isError || isEmpty(outdoorWeather)) {
      setShowSkeleton(false);
    }
  }, [isSuccess, isError, outdoorWeather]);

  const tz = useMemo(() => {
    return timezone || 'local';
  }, [timezone]);

  const hour12 = useMemo(() => {
    return !is24HourFormat;
  }, [is24HourFormat]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000 * 60 * 1); // Update every 1 minute
    return () => clearInterval(id);
  }, []);

  const resolvedTz = useMemo(
    () => (tz === 'local' ? Intl.DateTimeFormat().resolvedOptions().timeZone : tz),
    [tz]
  );

  const weatherReport = useMemo(() => {
    const tempStr =
      outdoorWeather?.temp !== undefined
        ? `${outdoorWeather.temp}° ${outdoorWeather.unit || ''}`.trim()
        : '';
    return tempStr ? { text: tempStr } : null;
  }, [outdoorWeather]);
  useRegisterReportSection('weather', weatherReport, [weatherReport]);

  const parts = useMemo(() => {
    const timeFmt = new Intl.DateTimeFormat(locale, {
      timeZone: resolvedTz,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12,
    });

    const dateFmt = new Intl.DateTimeFormat(locale, {
      timeZone: resolvedTz,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const timeMap = Object.fromEntries(timeFmt.formatToParts(now).map((p) => [p.type, p.value]));
    const dateMap = Object.fromEntries(dateFmt.formatToParts(now).map((p) => [p.type, p.value]));

    const rawHour = timeMap.hour ?? '--';
    const dayNum = parseInt(dateMap.day ?? '0', 10);

    const getOrdinal = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };

    const dayWithOrdinal = dayNum ? `${dayNum}${getOrdinal(dayNum)}` : '';

    return {
      hour: hour12 ? rawHour.replace(/^0/, '') : rawHour.padStart(2, '0'),
      minute: timeMap.minute ?? '--',
      second: timeMap.second ?? '--',
      dayPeriod: timeMap.dayPeriod ?? '',
      weekday: dateMap.weekday ?? '',
      fullDate:
        dayWithOrdinal && dateMap.month
          ? `${dateMap.month} ${dayWithOrdinal}, ${dateMap.year}`
          : '',
    };
  }, [now, resolvedTz, hour12, locale]);

  return (
    <div className="weather-card-container">
      <div
        className={`weather-card ${isEmpty(outdoorWeather) && !showSkeleton ? 'no-weather' : ''}`}
      >
        {outdoorWeather?.temp !== undefined && (
          <div className="weather-temperature-container">
            {renderWeatherIcon(outdoorWeather?.icon)}
            <span className="temperature-with-symbol">{`${outdoorWeather?.temp}° ${outdoorWeather?.unit}`}</span>
            <div className="accuweather-container">
              <AccuweatherIcon />
            </div>
          </div>
        )}
        {showSkeleton && (
          <div className="weather-skeleton">
            <div className="weather-skeleton-temperature">
              <div className="skeleton-block skeleton-icon" />
              <div className="skeleton-block skeleton-temp" />
              <div className="skeleton-block skeleton-brand" />
            </div>
          </div>
        )}

        {showSkeleton && (
          <div className="weather-skeleton-time">
            <div className="skeleton-block skeleton-day" />
            <div className="skeleton-block skeleton-clock" />
            <div className="skeleton-block skeleton-date" />
          </div>
        )}

        {parts && !showSkeleton && (
          <div className="weather-time-container">
            <span className="day">{parts.weekday}</span>
            <span className="time">{`${parts.hour}:${parts.minute} ${parts.dayPeriod ?? ''}`}</span>
            <span className="date">{parts.fullDate}</span>
          </div>
        )}
      </div>
      <ScheduleCard
        useruuid={useruuid}
        userTimezone={timezone}
        locale={locale}
        is24HourFormat={is24HourFormat}
      />
    </div>
  );
};

WeatherCard.propTypes = {
  timezone: PropTypes.string,
  locale: PropTypes.string,
  useruuid: PropTypes.string,
  hubRecord: PropTypes.object,
  is24HourFormat: PropTypes.bool,
  measurementSystem: PropTypes.string,
};

export default WeatherCard;
